import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    getRoomById,
    createRoom,
    updateRoom,
} from "../../services/hostelService";

const DEFAULT_ROOM_RENTS = {
    Single: 4000,
    Double: 3000,
    Triple: 1500,
    Dormitory: 1500,
};

const EMPTY_FORM = {
    roomNumber: "",
    floor: "",
    roomType: "Double",
    capacity: 2,
    roomRent: 3000,
    status: "Available",
    notes: "",
};

const ROOM_TYPES = ["Single", "Double", "Triple", "Dormitory"];
const ROOM_STATUSES = ["Available", "Full", "Maintenance"];

function Field({ label, name, value, onChange, error, required = false, type = "text", placeholder = "", min }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            <input
                className={`form-control ${error ? "error" : ""}`}
                type={type}
                name={name}
                min={min}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function RoomForm() {
    const { hostelId, id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [fetching, setFetching] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [occupiedBeds, setOccupiedBeds] = useState(0);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await getRoomById(id);
                const { roomNumber, floor, roomType, capacity, roomRent, status, notes, occupiedBeds } = res.data;
                const resolvedType = roomType || "Double";
                setForm({
                    roomNumber: roomNumber || "",
                    floor: floor || "",
                    roomType: resolvedType,
                    capacity: capacity || 1,
                    roomRent: roomRent !== undefined && roomRent !== null ? Number(roomRent) : (DEFAULT_ROOM_RENTS[resolvedType] || 3000),
                    status: status || "Available",
                    notes: notes || "",
                });
                setOccupiedBeds(occupiedBeds || 0);
            } catch (err) {
                toast.error(err.response?.data?.message || "Failed to load room");
                navigate(`/hostels/${hostelId}/rooms`);
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit, hostelId, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const next = {
                ...prev,
                [name]: name === "capacity" || name === "roomRent" ? (value === "" ? "" : Number(value)) : value,
            };

            // If changing roomType, auto-suggest the default roomRent if untouched or matching standard
            if (name === "roomType") {
                const oldDefault = DEFAULT_ROOM_RENTS[prev.roomType] || 3000;
                if (!prev.roomRent || prev.roomRent === oldDefault) {
                    next.roomRent = DEFAULT_ROOM_RENTS[value] || 3000;
                }
            }

            return next;
        });
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.roomNumber.trim()) nextErrors.roomNumber = "Room number is required";
        if (!form.capacity || Number(form.capacity) < 1) {
            nextErrors.capacity = "Capacity must be at least 1";
        }
        if (form.roomRent === "" || Number(form.roomRent) < 0) {
            nextErrors.roomRent = "Room rent must be 0 or more";
        }
        if (isEdit && Number(form.capacity) < occupiedBeds) {
            nextErrors.capacity = `Capacity cannot go below ${occupiedBeds} occupied bed(s)`;
        }
        return nextErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            toast.error("Please fix the errors before saving.");
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await updateRoom(id, form);
                toast.success("Room updated successfully");
            } else {
                await createRoom({ ...form, hostel: hostelId });
                toast.success("Room created successfully");
            }
            navigate(`/hostels/${hostelId}/rooms`);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save room");
        } finally {
            setSaving(false);
        }
    };

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading room...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Room" : "Add Room"}</h1>
                    <p className="page-subtitle">Maintain room number, monthly rent charge, bed capacity, room type, and status.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="form-grid">
                            <Field
                                label="Room Number"
                                name="roomNumber"
                                required
                                value={form.roomNumber}
                                onChange={handleChange}
                                error={errors.roomNumber}
                                placeholder="e.g. 101"
                            />
                            <Field
                                label="Floor"
                                name="floor"
                                value={form.floor}
                                onChange={handleChange}
                                placeholder="e.g. Ground, 1st"
                            />

                            <div className="form-group">
                                <label className="form-label">Room Type</label>
                                <select className="form-control" name="roomType" value={form.roomType} onChange={handleChange}>
                                    {ROOM_TYPES.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            <Field
                                label="Monthly Room Rent / Charge (₹)"
                                name="roomRent"
                                type="number"
                                required
                                min={0}
                                value={form.roomRent}
                                onChange={handleChange}
                                error={errors.roomRent}
                                placeholder="e.g. 4000 (Single), 3000 (Double), 1500 (Triple)"
                            />

                            <Field
                                label="Capacity"
                                name="capacity"
                                type="number"
                                required
                                min={occupiedBeds || 1}
                                value={form.capacity}
                                onChange={handleChange}
                                error={errors.capacity}
                            />

                            {isEdit && (
                                <div className="form-group">
                                    <label className="form-label">Status</label>
                                    <select className="form-control" name="status" value={form.status} onChange={handleChange}>
                                        {ROOM_STATUSES.map((status) => (
                                             <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                    {occupiedBeds > 0 && (
                                        <span className="form-hint">{occupiedBeds} bed(s) currently occupied.</span>
                                    )}
                                </div>
                            )}

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Notes</label>
                                <textarea
                                    className="form-control"
                                    name="notes"
                                    value={form.notes}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Optional notes about this room"
                                    style={{ resize: "vertical" }}
                                />
                            </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
                            <button type="button" className="btn btn-outline" onClick={() => navigate(`/hostels/${hostelId}/rooms`)}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? "Saving..." : isEdit ? "Update Room" : "Add Room"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
